/**
 * @file        worker/index.js    This module implements the DistributiveWorker API, used to create workers for
 *                                 earning DCCs.
 *
 * @author      Ryan Rossiter <ryan@kingsds.network>
 *              Paul <paul@distributive.network>
 * @date        May 2020
 *              June, July 2022, Feb-April 2023
 *
 * @module dcp/worker
 * @access public
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 *
 * DistributiveWorker State Transition Diagram:
 *
 * initial      started         stopping          stopped
 * ------------ --------------- ----------------- -----------------------------
 * ctor
 *   |-- start -----------------------------------> #end // beforeStart cancel
 *   |-- start --->
 *                |-- start ----------------------> #end // catch(error)
 *   |-- stop ------------------------------------> #end // never started
 *                |-- stop -------->
 *                                 |--------------> #end
 */
// @ts-check
'use strict';

const debugging = require('dcp/debugging').scope('worker');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const { SchedMsg } = require('dcp/dcp-client/schedmsg');
const { Synchronizer } = require('dcp/common/concurrency');
const EventEmitter = require('events');
const { localStorage } = require('dcp/common/dcp-localstorage');
const { confirmPrompt } = require('dcp/utils');
const { Address } = require('dcp/dcp-client/wallet/eth');
const dcpEnv = require('dcp/common/dcp-env');
const dcp_timers = require('dcp/common/dcp-timers');
const { setImmediate } = dcp_timers;
const DISABLE_WORKER_CACHE_KEY = 'disable_worker';
const { Supervisor } = require('./supervisor2');
const { leafMerge, deepClone } = require('dcp/utils');
const { Reference } = require('dcp/common/dcp-timers');
const management = require('./management');
const { friends } = require('./consts');

// Supervisor2 typedefs
/** @typedef {import('./supervisor2/slice2').Slice} Slice       */
/** @typedef {import('./supervisor2/sandbox2').Sandbox} Sandbox */

/** @typedef {string} opaqueId */ // 22 character base64 string
/** @typedef {import('dcp/dcp-client/wallet/eth').Address} Address                 */
/** @typedef {import('dcp/dcp-client/wallet/keystore').Keystore} Keystore          */
/** @typedef {import('dcp/utils/jsdoc-types').SandboxOptions} SandboxOptions       */
/** @typedef {import('dcp/utils/jsdoc-types').WorkerOptions} WorkerOptions         */
/** @typedef {import('dcp/dcp-client/worker/origin-access-manager').OriginAccessManager} OriginAccessManager */

function disableWorker ()
{
  localStorage.setItem(DISABLE_WORKER_CACHE_KEY, true);
}

/* DistributiveWorker configuration parameters which must exist, but are so esoteric that they don't
 * deserve to be listed in ./worker-default-config.js. This is the weakest precedence object used to
 * build the DistributiveWorker::config object.
 */
const internalConfig = {
};

/**
 * DistributiveWorker - the user-facing API class used to communicate with the supervisor.
 * This class inherits from both Reference and EventEmitter. It maintains an event loop
 * reference from the time it is instanciated until the ::stop() method finishes (::#end()).
 * @access public
 */
class DistributiveWorker extends EventEmitter
{
  /** Private class fields */
  #supervisor;
  /** @type {{ cpu: number, gpu: number }} */
  #effectiveCores;
  #dropSandboxes;
  #state;
  #id;

  static obtainWorkerId                        = management.obtainWorkerId;
  static resetLocalStorage                     = management.resetLocalStorage;
  static jnuConnect                            = management.jnuConnect;
  static checkUnclaimedEarnings                = management.checkUnclaimedEarnings;
  static transferUnclaimedEarnings             = management.transferUnclaimedEarnings;
  static obtainUnclaimedEarningsPaymentAddress = management.obtainUnclaimedEarningsPaymentAddress;

  static recommended = {
    get maxSandboxes()
    {
      var howMany;
      if (dcpEnv.platform === 'nodejs')
        howMany = Math.max( 1, Math.round(requireNative('os').totalmem() / (2.5 * 1000 * 1000 * 1000)) );
      else if (dcpEnv.isBrowserPlatform && navigator?.deviceMemory) /* https only */
        howMany = Math.max( 1, Math.round(navigator.deviceMemory / 2.5) );
      else if (dcpEnv.isBrowserPlatform && /Android|iPhone/.exec(navigator?.userAgent))
        howMany = 1;
      else if (dcpEnv.platform === 'pythonmonkey')
        howMany = 2;
      else
        howMany = 2;
      return howMany;
    }
  }

  /**
   * Returns a new DistributiveWorker instance.
   * This constructor may alter the config object, for version-based polyfilling and/or providing
   * key API properties, such as the cores object.
   */
  constructor(config, SandboxConstructor)
  {
    super({ captureRejections: false });
    this.#state = new Synchronizer('initial', [ 'initial', 'started', 'stopping', 'stopped' ]);
    this.on('error', (error) => { debugging() && console.debug('event:', error); }); /* side-effect; avoid emit->throw lunacy */

    Reference.call(this, debugging, () => this.id);
    this.ref();

    /* The worker's config is the aggregate of the following merge, in least-to-most precedence:
     * - internal config object
     * - ./worker-default-config.js
     * - user's dcpConfig.worker
     * - config argument to this ctor
     *
     * All of these sources are deepCloned before merging, so that different workers in the same process
     * do not accidentally leak configuration information to each other.  To alter a running worker's
     * config, alter this.config.
     *
     * The defaultConfig object is a user's dcpConfig merged on top of ./worker-default-config.js. That
     * file is buried in the dcp-client bundle and becomes dcpConfig.worker when dcp-client initializes.
     * The dcpConfig.worker node is a protected node, so it can't be overridden by the scheduler's web
     * config.
     */
    const defaultConfig  = deepClone(dcpConfig.worker);
    const overrideConfig = config ? deepClone(config) : {};
    this.config = leafMerge(deepClone(internalConfig), defaultConfig, overrideConfig);

    if (!SandboxConstructor)
    {
      if (dcpEnv.isBrowserPlatform)
        SandboxConstructor = require('./evaluators').BrowserEvaluator;
      else
        throw new Error('SandboxConstructor not specified for DistributiveWorker!');
    }

    /** dropSandboxes wrapper function, unique per instance of DistributiveWorker, suitable for use as
     * an event handler.
     */
    this.#dropSandboxes = () => this.#supervisor.dropSandboxes();
    /**
     * @type {any}
     * @access public
     */
    /**
     * @type {SchedMsg|undefined}
     * @access public
     */
    this.schedMsg = undefined;
    /**
     * It is important that dynamic changes in config are reflected in Supervisor operations. In the case where
     * cores are derived solely from utilization, mkActiveProp_cores provides an object which stays up to date with
     * utilization changes until a value is explicitly set.
     */
    DistributiveWorker.#mkActiveProp_vcores(this.config);
    DistributiveWorker.#mkActiveProp_cores(this.config);
    DistributiveWorker.#mkActiveProp_maxSandboxes(this.config);
    DistributiveWorker.#mkActiveProp_utilitization(this.config);

    // Modify options.cores with options.utilization.
    const that = this;
    this.#effectiveCores = {
      get cpu () { return that.config.utilization.cpu * that.config.cores.cpu || 0; },
      get gpu () { return that.config.utilization.gpu * that.config.cores.gpu || 0; },
    };

    /** @type {Supervisor} */
    this.#supervisor = new Supervisor(this, SandboxConstructor);
    debugging() && console.debug(`Worker options(sup-version:${this.supervisorVersion}):`, this.config.cores.cpu, this.config.cores.gpu, this.config);

    /** Object where worker application can override run info detected by management code, eg .type */
    this.runInfo = { type: dcpEnv.isBrowserPlatform ? 'browser' : 'standalone' };
  }

  /** @type {string} */
  get supervisorVersion()
  {
    return this.#supervisor.version
  }

  /**
   * Disables worker instances from being started. The user will need to manually intervene to re-enable workers.
   * @access public
   */
  static disableWorker ()
  {
    disableWorker();
  }

  register (...args)
  {
    return management.register(this, ...args)
  }

  updateRunInfo (...args)
  {
    return management.updateRunInfo(this, ...args)
  }

  /**
   * Starts the worker.
   * @access public
   *
   * @returns {boolean} true if the worker starts, false if it does not but doesn't throw.
   */
  async start()
  {
    if (!this.#state.is('initial'))
    {
      debugging() && console.debug('Worker already started, ignoring start request');
      return false;
    }
    debugging() && console.debug('Starting worker');

    if (localStorage.getItem(DISABLE_WORKER_CACHE_KEY))
    {
      await confirmPrompt('Worker has been disabled by the DCP Security Team; check the @DCP_Protocol Twitter feed for more information before continuing.')
      if (await confirmPrompt('Are you sure you would like to restart the worker?'))
      {
        localStorage.removeItem(DISABLE_WORKER_CACHE_KEY);
        console.warn('*** Restarting worker...');
      }
      else
      {
        debugging() && console.debug('Worker not started because it has been disabled');
        return false;
      }
    }

    try
    {
      let dontStart = false;

      this.emit('beforeStart', function cancel() { dontStart = true });
      if (dontStart)
      {
        debugging() && console.debug('Worker not started because the beforeStart callback canceled it.');
        this.#end();
        return false;
      }

      await management.applyDbConfig(this);
      if (!this.config.paymentAddress)
        this.config.paymentAddress = await management.obtainUnclaimedEarningsPaymentAddress();
      else if (this.config.claimedEarnings !== false && this.config.managed !== false)
      {
        const earned = await management.transferUnclaimedEarnings(this.config.paymentAddress);
        this.emit('claim', earned.payload);
      }

      if (!await this.updateRunInfo({
        lastStartTime: Date.now(),
        status:        'running',
      }))
      {
        throw new Error('unable to manage worker');
      }
      this.on('payment',  (amount) => management.updateEarnings(this, amount));
      this.on('stop',           () => management.updateEarnings(this, 0, true));
      this.on('sandbox', (sandbox) => management.registerSandbox(this, sandbox));
      this.on('metrics',      (ev) => management.updateTaskInfoCache(this, ev));
      this.on('drop-slice',   (ev) => management.updateTaskInfoCache(this, ev));
      this.on('error',     (error) => {
        const lastErrorTimestamp = Date.now();
        const lastError = { type: 'error' }; /* copy of error with all enumerable props */
        Object.entries(Object.getOwnPropertyDescriptors(error)).forEach(entry => {
          var [k,v] = entry;
          v = Object.assign({}, v, { enumerable: true });
          Object.defineProperty(lastError, k, v) });
        this.updateRunInfo({ lastErrorTimestamp, lastError });
      });
      this.on('warning', warning => {
        const lastErrorTimestamp = Date.now();
        const lastError = { type: 'warning', message: warning };
        this.updateRunInfo({ lastErrorTimestamp, lastError });
      });

      if (typeof this.config.paymentAddress === 'string' || this.config.paymentAddress instanceof String)
        this.config.paymentAddress = new Address(this.config.paymentAddress);
      if (!(this.config.paymentAddress instanceof Address))
      {
        const paKind = this.config.paymentAddress.constructor?.name || typeof this.config.paymentAddress;
        throw new Error(`paymentAddress must be an instance of wallet::Address, not ${paKind}`);
      }
      if (!this.schedMsg)
        this.schedMsg = new SchedMsg(this);
      this.schedMsg.start();
      if (dcpEnv.isBrowserPlatform
          && !(new RegExp(atob('KGRpc3RyaWJ1dGl2ZVwubmV0d29ya3xcLmRpc3RyaWJ1dGVkXC5jb21wdXRlcikk'), 'i')).test(window.location.origin)
          && globalThis.ZGlzdHJpYnV0aXZl !== window.location.origin)
      {
        setTimeout(() => { /* make it possible for users to know if we are being used surreptitiously */
          console.debug(atob('U3RhcnRpbmcgRENQIFdvcmtlci4gVmlzaXQgaHR0cHM6Ly9kaXN0cmlidXRpdmUubmV0d29'
                        + 'yay8gdG8gbGVhcm4gbW9yZSBhYm91dCBEQ1AuCg=='));
        }, 500);
      }

      if (dcpEnv.platform === 'nodejs')
        requireNative('process').on('SIGHUP', this.#dropSandboxes);
    }
    catch(error)
    {
      setImmediate(() => this.#end()); /* emit end event after error is thrown */
      throw error;
    }

    try
    {
      this.#state.set('initial', 'started');
      this.#supervisor.work();
      this.emit('start');
    }
    catch(error)
    {
      /* Exceptions/Rejections during start() are fatal errors which cause the worker to stop
       * immediately. The stop operation is delayed one tick to preserve error ordering. In
       * particular, we want the DistributiveWorker<end> event emitted after the exception is
       * thrown.
       */
      setImmediate(() => {
        try
        {
          this.stop(true);
        }
        catch(error2)
        {
          this.emit('error', error2);
        }
      });

      throw error;
    }

    return true;
  }

  /**
   * Stops the worker.
   * @access public
   * @param {boolean} [forceTerminate=false] Whether the worker should stop imediately or allow the current slices to finish.
   */
  stop(forceTerminate = false)
  {
    if (this.#state.is('stopped')) /* worker already stopped, ignore stop request */
      return;

    // This 'if' allows stop(false) then stop(true) to work.
    if (!forceTerminate && this.#state.is('stopping'))
    {
      debugging() && console.debug('Worker already stopping, ignoring stop request');
      return;
    }

    if (this.#state.in(['initial', 'stopping']))
    {
      let inStop = true;
      let that = this;

      debugging('shutdown') && console.log('emit DistributiveWorker<stop>');
      this.emit('stop', function abort() {
        if (inStop)
          forceTerminate = true;
        else
          that.stop(true);
      });
      inStop = false;
    }

    if (this.#state.is('initial')) /* worker never started, pretend we've stopped */
    {
      this.#end();
      return;
    }

    this.#state.set(['started', 'stopping'], 'stopping');
    try
    {
      debugging('shutdown') && console.debug(`Worker.stop(${forceTerminate}): stopping supervisor...`);

      return this.#supervisor.stopWork(forceTerminate).finally(() => {
        debugging('shutdown') && console.debug(`Worker.stop(${forceTerminate}): supervisor stopped ok`);
        this.#end();
      });
    }
    catch (error)
    {
      this.emit('error', error);
      this.#end();
    }
  }

  /**
   * Final method of DistributiveWorker which can be run. Once the 'end' even is emitted, everything
   * in the class should be unused and unusable.
   */
  #end()
  {
    if (this.#state.is('stopped'))
      return;
    if (dcpEnv.platform === 'nodejs')
      requireNative('process').off('SIGHUP', this.#dropSandboxes);
    this.#state.set(['initial', 'stopping'], 'stopped');
    this.schedMsg && this.schedMsg.stop();
    if (this.config.unmanaged)
    {
      debugging('shutdown') && console.log('emit DistributiveWorker<end> (unmanaged)');
      this.emit('end');
      this.removeAllListeners();
      this.unref();
    }
    else
    {
      management.markActiveTasksFinished(this, 'worker-stop');
      management.updateRunInfo(this, { status: 'end' })
        .then(() => management.flushTaskInfoCache(this))
        .then(() => {
          debugging('shutdown') && console.log('emit DistributiveWorker<end>');
          this.emit('end');
          this.removeAllListeners();
          this.unref();
        });
    }
  }

  /**
   * Calls this.stop(false) and places this.stop(true) in a timer so there is a deterministic
   * upper bound on how long it takes to stop.
   * @param {number} timeLimit - deterministic upper bound on stopping time
   * @returns {Promise<any>}
   */
  controlledStop (timeLimit = 86400 * 1000)
  {
    return new Promise((resolve) => {
      let handle = dcp_timers.setTimeout(() => {
        handle = null;
        this.stop(true);
        debugging() && console.debug('Worker.controlledStop: Called deterministic worker.stop(true).');
        resolve();
      }, timeLimit);

      handle.unref();

      return this.stop(false)
        .finally(() => {
          debugging() && console.debug('Worker.controlledStop: Non-deterministic worker.stop(false) has completed.');
          if (handle)
            dcp_timers.clearTimeout(handle);
          resolve();
        });
    });
  }

  /** @type {OriginAccessManager} */
  get originManager () { return this.#supervisor.originManager; }
  //
  // This is the official supported interface for accessing supervisor.
  //
  /** @type {{ cpu: number, gpu: number }} */
  get effectiveCores ()   { return this.#effectiveCores; }
  /** @type {Sandbox[]} */
  get sandboxes ()        { return this.#supervisor.sandboxes; }
  /** @type {Sandbox[]} */
  get workingSandboxes () { return this.#supervisor.workingSandboxes; }
  /** @type {Slice[]} */
  get slices ()           { return this.#supervisor.slices; }
  /** @type {Slice[]} */
  get queuedSlices ()     { return this.#supervisor.queuedSlices; }
  /** @type {Slice[]} */
  get workingSlices ()    { return this.#supervisor.workingSlices; }
  /** @type {opaqueId} */
  set id (id)    { this.#id = id; }
  /** @type {opaqueId} */
  get id ()
  {
    if (!this.#id)
      this.#id = management.obtainWorkerId();
    return this.#id;
  }

  /**
   * @deprecated
   * Please do not use this.
   * @type {Supervisor}
   */
  get badSupervisorBackdoor () { return this.#supervisor; }

  /** Internal API for accessing supervisor; do not export! */
  get [friends.supervisor] () { return this.#supervisor || null; }

  /**
   * Tries to fetch a task from TD (task distributer).
   * @todo Get rid of this (XXXpfr)
   * @param {number} [numCores]
   * @returns {Promise<any>}
   */
  fetchTask (numCores)
  {
    return this.#supervisor.fetchTask();
  }
  /**
   * Remove sandbox from supervisor and terminate it.
   * @param {Sandbox} sandbox
   */
  returnSandbox (sandbox)
  {
    return this.#supervisor.returnSandbox(sandbox);
  }
  /**
   * Remove sandbox from supervisor and return it to the scheduler.
   * @param {Slice} slice
   * @param {string} reason
   * @returns {Promise<any>}
   */
  returnSlice (slice, reason = 'unknown')
  {
    return this.#supervisor.returnSlice(slice, reason);
  }

  /**
   * When paused, the worker will not execute new slices or fetch new work.
   * But it will be alive and well, waiting to be unpaused.
   * No slices will be returned to scheduler.
   */
  pause ()
  {
    this.#supervisor.pause();
  }
  /**
   * Only valid to call unpause after pause has been called.
   * The worker will now execute new slices and fetch new work.
   */
  unpause ()
  {
    this.#supervisor.unpause();
  }

  /**
   * Build an object suitable for use as options.cores based on the presumed number of logical cores in
   * the machine, and options.utilization. This is the only place options.utilization
   * should be used, all other calculations throughout the worker should be based on options.cores. The
   * default utilization is in ./worker-default-config.js.
   *
   * options.hardware can be used to override the detected number of cores
   *
   * @note We current assume the machine has one GPU of infinite capability, and use evaluator capability
   *       detection to use or not use this amazing GPU. That will obviously change as our capabilities
   *       evolves.
   *
   * @note There are very important notes re core-count selection in How-supervisor-works.md.
   *
   * @note This is a static property of DistributiveWorker because it is based on the physical properties of the
   *       machine and the options object and does not have any relationship to a particular instance
   *       of DistributiveWorker.
   *
   * @note options.cores.cpu === 0 means the worker is disabled.
   *       options.cores.gpu === 0 means the worker is disabled for GPU jobs.
   *
   * @param {WorkerOptions} options
   * @returns {{ cpu: number, gpu: number }}
   */
  static #mkActiveProp_cores (options)
  {
    const rawCores = { cpu: options.hardware.vCores, gpu: 1 /* one GPU until we know better */ };
    if (typeof options.cores?.cpu !== 'undefined')
      rawCores.cpu = options.cores.cpu;
    if (typeof options.cores?.gpu !== 'undefined')
      rawCores.gpu = options.cores.gpu;

    /** Create an "active" return object suitable for use as options.cores. Any writes to this object
     * persist as-is, but in the usual case, reading from the object causes the values to change
     * when hardware.vCores and/or utilization change.
     * @type {{ cpu: number, gpu: number }}
     */
    const ret = { cpu: 0, gpu: 0 };

    Object.defineProperty(ret, 'cpu', {
      get: ()      => rawCores.cpu,
      set: (value) => {
        delete ret.cpu;
        ret.cpu = value;
      },
      configurable: true,
      enumerable: true,
    });

    Object.defineProperty(ret, 'gpu', {
      get: ()      => rawCores.gpu,
      set: (value) => {
        delete ret.gpu;
        ret.gpu = value;
      },
      configurable: true,
      enumerable: true,
    });

    // Alias ret to options.cores
    options.cores = ret;

    // Check validity.
    if (!(options.cores.cpu >= 0))
      options.cores.cpu = 0;
    if (!(options.cores.gpu >= 0))
      options.cores.gpu = 0;
  }

  /**
   * Compute utilization { cpu, gpu } and fill-in defaults.
   * utilization.cpu === 0 means the worker is disabled.
   * utilization.gpu === 0 means the worker is disabled for GPU jobs.
   * @param {WorkerOptions} options
   */
  static #mkActiveProp_utilitization (options)
  {
    const _defaultUtilization = { cpu: 1, gpu: 0.75 };
    if (typeof options.utilization === 'undefined')
      options.utilization = _defaultUtilization;
    else
    {
      if (typeof options.utilization?.cpu === 'undefined')
        options.utilization.cpu = _defaultUtilization.cpu;
      if (typeof options.utilization?.gpu === 'undefined')
        options.utilization.gpu = _defaultUtilization.gpu;
    }

    // Check validity.
    if (!(options.utilization.cpu >= 0))
      options.utilization.cpu = 0;
    if (!(options.utilization.gpu >= 0))
      options.utilization.gpu = 0;
  }

  /**
   * Compute hardware { vCores }.
   * When hardware.vCore is falsy, the value will be filled-in to have computed default value.
   * @param {WorkerOptions} options
   */
  static #mkActiveProp_vcores (options)
  {
    if (!options.hardware)
      options.hardware = { vCores: 0 };
    if (!options.hardware.vCores)
    {
      switch(dcpEnv.platform)
      {
        case 'nodejs':
          options.hardware.vCores = requireNative('os').cpus().length;
          break;
        case 'vanilla-web':
        case 'bravojs':
          options.hardware.vCores = navigator.hardwareConcurrency; /* can be less than actual hardware capabilities */
          if (options.hardware.vCores > 2 && /(Android).*(Chrome|Chromium)/.exec(navigator.userAgent))
            options.hardware.vCores = 2; /* Restricted on Android-Chrome due to bad OOM-handling; see DCP-3241 */
          break;
        default:
          options.hardware.vCores = 1;
      }
    }

    // Check validity.
    if (!(options.hardware.vCores >= 0))
      options.hardware.vCores = 0;
  }

  /**
   * When options.maxSandboxes is undefined, sets a default maxSandboxes for nodejs and browsers
   * that support navigator.deviceMemory. Otherwise maxSandboxes is 1.
   * @param {WorkerOptions} options
   */
  static #mkActiveProp_maxSandboxes (options)
  {
    debugging() && console.debug('mkActiveProp_maxSandboxes:', dcpEnv.platform, dcpEnv.isBrowserPlatform, options);

    if (typeof options.maxSandboxes === 'string')
      options.maxSandboxes = Number(options.maxSandboxes);
    if (typeof options.maxSandboxes === 'undefined')
      options.maxSandboxes = DistributiveWorker.recommended.maxSandboxes;
    // Check validity.
    if (!(options.maxSandboxes >= 0))
      options.maxSandboxes = 0;
  }

  /**
   * DistributiveWorker::inspect is an introspection API, giving developers mutation-safe access to
   * worker internals.
   *
   * computeGroups: shows the compute groups credentials that will be used on the next task request.
   *                This includes the the Global Compute Group.
   */
  get inspect()
  {
    const that = this;
    return {
      get computeGroups() {
        return require('dcp/dcp-client/compute-groups').generateTaskRequestWorkerComputeGroups(that.config, false);
      }
    }
  }

  /**
   * Create a WorkerError.
   *
   * In many cases the worker may not be stable after emitting errors that are not completely
   * understood.  Hence we advise to restart a worker when those errors occur. The errors which have
   * been vetted as safe are instances of WorkerError; all others can be assumed to be fatal.
   *
   * @param {Error} error
   * @returns {WorkerError}
   */
  static createWorkerError (error)
  {
    return new WorkerError(error);
  }

  /**
   * Is error a WorkerError?
   * This method is non-static for ease-of-use in worker apps.
   * @param {Error} error
   * @returns {boolean}
   */
  isWorkerError (error)
  {
    return error instanceof WorkerError;
  }
}
Object.assign(DistributiveWorker.prototype, Reference.prototype);

/**
 * Backwards compability wrapper, polyfilling Worker API 1.0 via DistributiveWorker API 2.0. Keep
 * until winter 2025 /wg.
 *
 * @access public
 * @param {Keystore} idKs - identity Keystore - obsolete - must be falsey or match current identity
 * @param {WorkerOptions} config - options specified for the worker; may be mutated
 */
function Worker(idKs, config)
{
  const SandboxConstructor = config?.sandboxOptions?.SandboxConstructor;
  const dw = new DistributiveWorker(config, SandboxConstructor);
  var bad = false;

  if (!new.target)
    throw new Error('this function must be invoked as a constructor');

  if (idKs)
  {
    require('dcp/dcp-client/wallet').getId().then(idKs2 => {
      if (!idKs2.address.eq(idKs.address))
        bad = true;
    });
  }

  if (!Worker.apiWarned)
  {
    if (dcpEnv.isBrowserPlatform && window.location.origin === 'https://dcp.cloud')
      Worker.apiWarned = true;
    else if (dcpEnv.platform === 'nodejs' && (requireNative('path').basename(requireNative.main.filename) === 'dcp-worker'))
      Worker.apiWarned = true;
    else
    {
      console.warn('Warning: using obsolete Worker API 1.0; please upgrade to DistributiveWorker API 2.0. This API will be removed in November 2025.');
      Worker.apiWarned = true;
    }
  }

  if (config)
    config.cores = Object.assign(config.cores || {}, dw.config.cores);

  return new Proxy(dw, {
    get: (target, prop) => {
      switch(prop)
      {
        case 'workerOptions':
          return dw.config;
        case 'workerId':
          return dw.id;
        case 'paymentAddress':
          return dw.config.paymentAddress;
        case 'identityKeystore':
          return idKs;
        default:
          if (!bad)
            return typeof dw[prop] === 'function' ? dw[prop].bind(dw) : dw[prop];
      }
      throw new Error('ad-hoc identity specification not supported in DCP Worker polyfill');
    },
    set: (target, prop, value) => {
      if (prop === 'paymentAddress')
        dw.config.paymentAddress = value;
      else
        dw[prop] = value;
      if (bad)
        throw new Error('ad-hoc identity specification not supported in DCP Worker polyfill');
      return true;
    }
  });
}

/**
 * Probe the evaluator by running sandbox.describe() and then destroying the sandbox. A side-effect of
 * describing the sandbox is to populate the supervisor's worktimes object, which describes the worktimes
 * that the sandbox (potentially remote dcp-client) knows about.
 *
 * @param {function} SandboxConstructor         the sandbox ctor (eg standalone or browser worker) that
 *                                              will be passed to the DistributiveWorker ctor next
 *
 * @returns {object} with capabilties and worktimes
 *
 * @note  this is an awful hack that solved a very specific problem within very tight constraints. If
 *        this code ever needs maintenance (because the supervisor or sandbox implementation changed),
 *        it should probably just become a static method of the DistributiveWorker class. /wg jun 2025
 */
async function probeEvaluator(SandboxConstructor)
{
  const { Sandbox } = require('./supervisor2/sandbox2');
  const fakeWorker = new DistributiveWorker({}, SandboxConstructor);
  fakeWorker.unref();
  const fakeSupervisor = fakeWorker.badSupervisorBackdoor;
  const sandbox = new Sandbox(fakeSupervisor, Object.assign({ SandboxConstructor }, dcpConfig.supervisor.sandbox));

  fakeWorker.debugLabel = fakeWorker.debugLabel ? `${fakeWorker.debugLabel} - probeEvaluator` : 'probeEvaluator';
  fakeWorker.unref();
  fakeSupervisor.on('evalDown', () => console.log('evaluator end'));
  await sandbox.start();
  const capabilities = await sandbox.describe(); /* side-effect fills in fakeSupervisor.worktimes */

  return {
    capabilities,
    worktimes: fakeSupervisor.worktimes,
  };
}

/**
 * class WorkerError
 * In many cases the worker may not be stable after emitting errors that are not completely understood.
 * Hence we advise to restart a worker when those errors occur. The errors which have been vetted as
 * safe are instances of WorkerError; all others can be assumed to be fatal.
 */
class WorkerError extends Error
{
  constructor (error)
  {
    super(error.message);
    const pds = Object.getOwnPropertyDescriptors(error);
    for (let propName in pds)
      pds[propName].enumerable = true
    Object.defineProperties(this, pds);
    this.name = WorkerError.name;
    this.stack = this.stack.replace(/(^DCPError)|(^Error)/, WorkerError.name);
  }
}

exports.DistributiveWorker  = DistributiveWorker;
exports.Worker              = Worker;
exports.disableWorker       = disableWorker;
exports.originAccessManager = require('./origin-access-manager');
exports.probeEvaluator      = probeEvaluator;

exports.version = {
  api: '2.0.0',
  provides: '1.0.0'  /* dcpConfig.scheduler.compatibility.operations.work */
};
